import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, Loader2, Crown } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { LOGO_URL } from "../data/content";
import { FORM_QUESTIONS } from "../data/formQuestions";

const ELIGIBLE_BUDGETS = [
  "185 - 220 € PAR MOIS durant 5 mois",
  "de 220 à 370€ PAR MOIS durant 3 ou 4 mois",
];

const flatKeys = () => {
  const keys = [];
  FORM_QUESTIONS.forEach((q) => {
    if (q.type === "group") q.fields.forEach((f) => keys.push(f));
    else if (q.type !== "group") keys.push(q);
  });
  return keys;
};

const initialState = () => {
  const s = {};
  flatKeys().forEach((f) => {
    s[f.key] = f.type === "checkbox" ? [] : "";
  });
  return s;
};

export default function Candidature() {
  const [form, setForm] = useState(initialState);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [missing, setMissing] = useState([]);
  const navigate = useNavigate();

  // Load Meta Pixel only on this form page (PageView on mount).
  useEffect(() => {
    if (!window.fbq) {
      /* eslint-disable */
      !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
      /* eslint-enable */
      window.fbq("init", "2203121083234815");
    }
    window.fbq("track", "PageView");
  }, []);

  const update = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setMissing((m) => m.filter((k) => k !== key));
    if (key === "budget_mensuel" && ELIGIBLE_BUDGETS.includes(value) && window.fbq) {
      window.fbq("trackCustom", "à du budget");
    }
  };

  const toggleCheckbox = (key, opt) => {
    setForm((p) => {
      const arr = p[key] || [];
      return { ...p, [key]: arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt] };
    });
    setMissing((m) => m.filter((k) => k !== key));
  };

  const validate = () => {
    const missingKeys = [];
    let emailInvalid = false;
    for (const f of flatKeys()) {
      const v = form[f.key];
      if (f.type === "checkbox") {
        if (!v || v.length === 0) missingKeys.push(f.key);
      } else if (!v || !String(v).trim()) {
        missingKeys.push(f.key);
      } else if (f.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        emailInvalid = true;
        missingKeys.push(f.key);
      }
    }
    return { missingKeys, emailInvalid };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const { missingKeys, emailInvalid } = validate();
    if (missingKeys.length > 0) {
      setMissing(missingKeys);
      setError(
        emailInvalid && missingKeys.length === 1
          ? "Veuillez saisir une adresse e-mail valide."
          : "Toutes les questions sont obligatoires. Merci de compléter les champs surlignés en rouge."
      );
      const firstEl = document.querySelector(`[data-missing-key="${missingKeys[0]}"]`);
      if (firstEl) firstEl.scrollIntoView({ behavior: "smooth", block: "center" });
      else window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setLoading(true);
    try {
      await api.post("/applications", form);
      const eligible = ELIGIBLE_BUDGETS.includes(form.budget_mensuel);
      if (eligible) {
        navigate("/reservation");
        return;
      }
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0B0E17] text-[#FDFBF7] flex items-center justify-center px-5">
        <div className="light-rays -z-10 opacity-40" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="glass max-w-lg rounded-3xl p-10 text-center"
          data-testid="candidature-success"
        >
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37]">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="mt-6 font-heading text-3xl font-light">Candidature reçue !</h1>
          <p className="mt-4 font-body text-[#A1A9C3]">
            Merci pour votre sincérité. Notre équipe étudie votre candidature et vous
            recontactera très prochainement.
          </p>
          <Link
            to="/"
            data-testid="back-home-button"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-7 py-3 font-body font-semibold text-[#0B0E17] transition-all hover:bg-[#e6c451]"
          >
            <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#0B0E17] text-[#FDFBF7]">
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=2203121083234815&ev=PageView&noscript=1"
          alt=""
        />
      </noscript>
      <div className="light-rays -z-10 opacity-30" />
      <div className="mx-auto max-w-3xl px-5 py-14 md:py-20">
        <Link
          to="/"
          data-testid="form-back-link"
          className="inline-flex items-center gap-2 font-body text-sm text-[#A1A9C3] transition-colors hover:text-[#D4AF37]"
        >
          <ArrowLeft className="h-4 w-4" /> Retour
        </Link>

        <div className="mt-8 text-center">
          <img src={LOGO_URL} alt="Logo" className="mx-auto h-20 w-20 object-contain" />
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#1A2033]/40 px-4 py-1.5">
            <Crown className="h-4 w-4 text-[#D4AF37]" />
            <span className="font-body text-xs uppercase tracking-[0.24em] text-[#C9A04E]">
              Formulaire de candidature
            </span>
          </div>
          <h1 className="mt-6 font-heading text-3xl font-light leading-tight md:text-4xl">
            Merci de répondre avec sincérité
          </h1>
          <p className="mx-auto mt-4 max-w-xl font-body text-sm text-[#A1A9C3] md:text-base">
            Prenez le temps de répondre consciemment à ces 15 questions afin de nous
            permettre de mieux comprendre votre situation et vos besoins musicaux.
          </p>
        </div>

        {error && (
          <div
            data-testid="form-error"
            className="mt-8 rounded-xl border border-red-500/40 bg-red-500/10 px-5 py-4 font-body text-sm text-red-200"
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-10 space-y-6">
          {FORM_QUESTIONS.map((q) => {
            const groupMissing =
              q.type === "group" && q.fields.some((f) => missing.includes(f.key));
            const qMissing = q.type !== "group" && missing.includes(q.key);
            return (
            <div
              key={q.key}
              className={`glass rounded-2xl p-6 md:p-8 transition-colors ${
                qMissing || groupMissing ? "ring-1 ring-red-500/60" : ""
              }`}
              data-testid={`question-${q.n}`}
            >
              <label className="block font-heading text-xl leading-snug text-[#FDFBF7]">
                <span className="mr-2 text-[#D4AF37]">{q.n}.</span>
                {q.label}
                <span className="ml-1 text-red-400">*</span>
              </label>

              {q.type === "group" && (
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  {q.fields.map((f) => (
                    <div key={f.key}>
                      <span className="mb-1 block font-body text-xs uppercase tracking-[0.14em] text-[#A1A9C3]">
                        {f.label} <span className="text-red-400">*</span>
                      </span>
                      <input
                        type={f.type}
                        value={form[f.key]}
                        onChange={(e) => update(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        data-testid={`input-${f.key}`}
                        data-missing-key={f.key}
                        className={`w-full rounded-xl border bg-[#0B0E17]/60 px-4 py-3 font-body text-[#FDFBF7] outline-none transition-colors placeholder:text-[#A1A9C3]/50 focus:border-[#D4AF37] ${
                          missing.includes(f.key) ? "border-red-500/70" : "border-[#D4AF37]/20"
                        }`}
                      />
                    </div>
                  ))}
                </div>
              )}

              {(q.type === "text" || q.type === "email" || q.type === "tel") && (
                <input
                  type={q.type}
                  value={form[q.key]}
                  onChange={(e) => update(q.key, e.target.value)}
                  placeholder={q.placeholder}
                  data-testid={`input-${q.key}`}
                  data-missing-key={q.key}
                  className={`mt-5 w-full rounded-xl border bg-[#0B0E17]/60 px-4 py-3 font-body text-[#FDFBF7] outline-none transition-colors placeholder:text-[#A1A9C3]/50 focus:border-[#D4AF37] ${
                    qMissing ? "border-red-500/70" : "border-[#D4AF37]/20"
                  }`}
                />
              )}

              {q.type === "textarea" && (
                <textarea
                  rows={4}
                  value={form[q.key]}
                  onChange={(e) => update(q.key, e.target.value)}
                  placeholder={q.placeholder}
                  data-testid={`input-${q.key}`}
                  data-missing-key={q.key}
                  className={`mt-5 w-full resize-none rounded-xl border bg-[#0B0E17]/60 px-4 py-3 font-body text-[#FDFBF7] outline-none transition-colors placeholder:text-[#A1A9C3]/50 focus:border-[#D4AF37] ${
                    qMissing ? "border-red-500/70" : "border-[#D4AF37]/20"
                  }`}
                />
              )}

              {q.type === "radio" && (
                <div className="mt-5 space-y-3" data-missing-key={q.key}>
                  {q.options.map((opt) => {
                    const active = form[q.key] === opt;
                    return (
                      <button
                        type="button"
                        key={opt}
                        onClick={() => update(q.key, opt)}
                        data-testid={`option-${q.key}-${opt}`}
                        className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left font-body text-sm transition-all ${
                          active
                            ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#FDFBF7]"
                            : "border-[#D4AF37]/15 bg-[#0B0E17]/40 text-[#A1A9C3] hover:border-[#D4AF37]/40"
                        }`}
                      >
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                            active ? "border-[#D4AF37] bg-[#D4AF37]" : "border-[#A1A9C3]/40"
                          }`}
                        >
                          {active && <span className="h-2 w-2 rounded-full bg-[#0B0E17]" />}
                        </span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}

              {q.type === "checkbox" && (
                <div className="mt-5 flex flex-wrap gap-3" data-missing-key={q.key}>
                  {q.options.map((opt) => {
                    const active = (form[q.key] || []).includes(opt);
                    return (
                      <button
                        type="button"
                        key={opt}
                        onClick={() => toggleCheckbox(q.key, opt)}
                        data-testid={`option-${q.key}-${opt}`}
                        className={`rounded-full border px-5 py-2.5 font-body text-sm transition-all ${
                          active
                            ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#FDFBF7]"
                            : "border-[#D4AF37]/15 bg-[#0B0E17]/40 text-[#A1A9C3] hover:border-[#D4AF37]/40"
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            );
          })}

          <button
            type="submit"
            disabled={loading}
            data-testid="submit-candidature-button"
            className="flex w-full items-center justify-center gap-3 rounded-full bg-[#D4AF37] px-9 py-4 font-body text-lg font-semibold text-[#0B0E17] transition-all hover:bg-[#e6c451] disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> Envoi en cours…
              </>
            ) : (
              "Envoyer ma candidature"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
